import { bindMarketoFormAnalytics } from './marketo-form-analytics';
import { bindMarketoFormErrorDisplay } from './marketo-form-error-display';
import { bindMarketoFormFocusRings } from './marketo-form-focus-rings';
import { showThankYouMessage } from './marketo-thank-you';
import {
  DEFAULT_MARKETO_BASE_URL,
  DEFAULT_MARKETO_SCRIPT_URL,
  loadMarketoScript
} from './shared-marketo-forms2';

const FORM_LOAD_TIMEOUT_MS = 15000;

type MarketoState = 'loading' | 'loaded' | 'config-error' | 'load-error' | 'success';

function debug(root: HTMLElement, message: string, details?: unknown): void {
  if (root.dataset.marketoDebug !== 'true') return;

  if (details === undefined) {
    console.debug(`[Marketo inline form] ${message}`);
    return;
  }

  console.debug(`[Marketo inline form] ${message}`, details);
}

function reportError(root: HTMLElement, message: string, error?: unknown): void {
  if (root.dataset.marketoDebug !== 'true') return;
  console.error(`[Marketo inline form] ${message}`, error);
}

function setState(root: HTMLElement, state: MarketoState, message?: string): void {
  root.dataset.marketoState = state;

  const status = root.querySelector<HTMLElement>('[data-marketo-form-status]');
  if (!status) return;

  if (state === 'loaded' || state === 'success') {
    status.setAttribute('hidden', '');
    return;
  }

  status.removeAttribute('hidden');
  status.dataset.state = state.endsWith('error') ? 'error' : 'loading';
  if (message) status.textContent = message;
}

function validateTarget(root: HTMLElement, formId: number): HTMLFormElement {
  const targetId = `mktoForm_${formId}`;
  const targets = document.querySelectorAll(`#${CSS.escape(targetId)}`);
  const target = root.querySelector<HTMLFormElement>(`#${CSS.escape(targetId)}`);

  if (!target) throw new Error(`Missing Marketo target #${targetId}`);
  if (targets.length > 1) throw new Error(`Duplicate Marketo target #${targetId}`);

  return target;
}

function loadForm(
  root: HTMLElement,
  baseUrl: string,
  munchkinId: string,
  formId: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`Marketo loadForm callback timed out after ${FORM_LOAD_TIMEOUT_MS}ms`));
    }, FORM_LOAD_TIMEOUT_MS);

    window.MktoForms2?.loadForm(baseUrl, munchkinId, formId, form => {
      window.clearTimeout(timeout);
      root.dataset.marketoFormLoaded = 'true';
      root.dataset.marketoFormLoading = 'false';
      setState(root, 'loaded');
      debug(root, 'Form loaded', { formId });
      if (form) {
        bindMarketoFormErrorDisplay(form, root);
        bindMarketoFormFocusRings(form, root);
        bindMarketoFormAnalytics(form, root, {
          // CMS thank-you must own onSuccess: competing listeners (often GTM) otherwise
          // async-redirect to followUpUrl ~1s later and wipe the message.
          ownSuccessHandlers: Boolean(root.querySelector('[data-marketo-thank-you]')),
          onSuccess: () => {
            // CMS thank-you present → swap in place and block Marketo redirect.
            // No template → leave return undefined so Marketo keeps default follow-up.
            if (showThankYouMessage(root)) {
              debug(root, 'Thank you message shown; blocking Marketo follow-up redirect');
              return false;
            }

            debug(root, 'No thank you message; keeping Marketo default follow-up');
            return undefined;
          }
        });
      }

      if (settled) return;
      settled = true;
      resolve();
    });
  });
}

export default async function marketoInlineForm(root: HTMLElement): Promise<void> {
  if (root.dataset.marketoFormLoaded === 'true') return;
  if (root.dataset.marketoFormLoading === 'true') return;

  const formId = Number(root.dataset.marketoFormId);
  const munchkinId = root.dataset.marketoMunchkinId?.trim();
  const baseUrl = root.dataset.marketoBaseUrl?.trim() || DEFAULT_MARKETO_BASE_URL;
  const scriptUrl =
    root.dataset.marketoScriptUrl?.trim() || `${baseUrl}/js/forms2/js/forms2.min.js`;

  debug(root, 'Initializing', {
    formId: root.dataset.marketoFormId,
    hasMunchkinId: Boolean(munchkinId),
    baseUrl,
    scriptUrl
  });

  try {
    if (!Number.isInteger(formId) || formId <= 0 || !munchkinId) {
      throw new Error('Marketo form ID or Munchkin ID is missing or invalid');
    }

    validateTarget(root, formId);
  } catch (error) {
    setState(
      root,
      'config-error',
      root.dataset.marketoConfigErrorLabel ?? 'Form configuration is missing.'
    );
    reportError(root, 'Invalid configuration', error);
    return;
  }

  try {
    root.dataset.marketoFormLoading = 'true';
    setState(root, 'loading');
    debug(root, 'Loading Forms2 script');
    await loadMarketoScript(scriptUrl || DEFAULT_MARKETO_SCRIPT_URL);

    if (!window.MktoForms2) {
      throw new Error('Marketo Forms2 API unavailable after script load');
    }

    debug(root, 'Calling MktoForms2.loadForm', { formId });
    await loadForm(root, baseUrl, munchkinId, formId);
  } catch (error) {
    root.dataset.marketoFormLoading = 'false';
    setState(
      root,
      'load-error',
      root.dataset.marketoLoadErrorLabel ?? 'The form could not be loaded. Please try again later.'
    );
    reportError(root, 'Unable to load form', error);
  }
}
